import { useEffect, type FunctionComponent } from "react";
import { Alert, Form, Input, Select } from "antd";
import { type FormInstance } from "antd/lib/form";
import { useTranslation } from "react-i18next";

import { AI_AUTOMATION_TASK_LIMITS } from "@fitness-app/data-models/entities/AutomationTemplate";
import { traineeAiReportAllowedDays } from "@fitness-app/data-models/entities/TraineeAiReport";

import { ScheduleTimingFields } from "../ScheduleTimingFields/ScheduleTimingFields";
import { TaskSenderField } from "../TaskSenderField/TaskSenderField";
import { TimeField } from "../TimeField/TimeField";
import { type AiReportTaskFormModel } from "./types";

interface OwnProps {
  formController?: FormInstance<AiReportTaskFormModel>;
  onSubmit: (formData: AiReportTaskFormModel) => void;
  // Partial: package-add passes only a scheduleTiming preset, not a full model.
  model?: Partial<AiReportTaskFormModel> | null;
  // TRE-2072 — the advisor sender only reaches clients of an advisorEnabled automation.
  advisorEnabled?: boolean | null;
}

type Props = OwnProps;

// TRE-2044 — scheduled AI report for the client. Deliberately no prompt/tone
// fields: the report has a fixed structure (what she did, one thing to
// appreciate, one small next step) written in the advisor voice; the content
// team only picks the lookback window, the fallback and (TRE-2072) the sender.
const AiReportTaskForm: FunctionComponent<Props> = ({ formController, onSubmit, model, advisorEnabled }) => {
  const { t } = useTranslation(["automation", "common"]);

  useEffect(() => {
    // reset-then-apply so a preset (or edited task) never leaves stale values
    // from a previous open in the shared form controller.
    formController?.resetFields();
    if (model) {
      formController?.setFieldsValue(model);
    }
  }, [model, formController]);

  return (
    <div>
      <Form<AiReportTaskFormModel>
        name="ai-report-task-form"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 14 }}
        labelWrap
        layout="horizontal"
        form={formController}
        initialValues={{
          name: "",
          days: 7,
          fallbackMessage: "",
          sender: "trainer",
          sentTimeConfig: { mode: "absolute", avoidSendingOnTheNight: true },
          ...model,
        }}
        onFinish={onSubmit}
      >
        <Form.Item wrapperCol={{ offset: 8, span: 14 }}>
          <Alert type="info" showIcon message={t("programAutomation.aiReport.formInfo")} />
        </Form.Item>

        <Form.Item
          name="name"
          label={t("programAutomation.aiReport.name")}
          rules={[
            {
              required: true,
              message: t<string>("common:validationErrors.fieldIsRequired"),
            },
          ]}
        >
          <Input placeholder={t("programAutomation.aiReport.namePlaceholder")} />
        </Form.Item>

        <Form.Item name="days" label={t("programAutomation.aiReport.range")} rules={[{ required: true }]}>
          <Select
            options={traineeAiReportAllowedDays.map((value) => ({
              value,
              label: t("programAutomation.aiReport.lastDays", { count: value }),
            }))}
          />
        </Form.Item>

        <Form.Item
          name="fallbackMessage"
          label={t("programAutomation.aiReport.fallbackMessage")}
          extra={t("programAutomation.aiReport.fallbackOptional")}
          rules={[{ max: AI_AUTOMATION_TASK_LIMITS.fallbackMessage.max }]}
        >
          <Input.TextArea rows={3} placeholder={t("programAutomation.aiReport.fallbackPlaceholder")} />
        </Form.Item>

        <TaskSenderField formController={formController} advisorEnabled={advisorEnabled} />

        <TimeField showOnlyAbsolute={false} label={t<string>("automation:automatedMessageForm.operationTime")} />

        <ScheduleTimingFields formController={formController} />
      </Form>
    </div>
  );
};

export default AiReportTaskForm;
